import { useEffect, useState } from "react";
import usePublicConfig from "storefront/hooks/usePublicConfig";
import findAlgoliaIndexName from "storefront/Config/PublicConfig/findAlgoliaIndexName";
import useSearch from "storefront/hooks/useSearch";
import useAlgoliaClient from "./useAlgoliaClient";
import { getUuidFromUrl, normalizeQuery } from "./utils";

type SuggestionHit = {
  query: string;
};

type DesignerHit = {
  name: string;
  id: number;
  slug: string;
};

type Results = {
  querySuggestions: Array<SuggestionHit>;
  designers: Array<DesignerHit>;
};

type HeaderSearch = {
  query: string;
  setQuery: (query: string) => void;
  results: Results;
};

type QueryCache = {
  [query: string]: {
    querySuggestions: Array<SuggestionHit>;
    designers: Array<DesignerHit>;
  };
};

const queryCache: QueryCache = {};

const useHeaderSearch = (): HeaderSearch => {
  const [query, setQuery] = useState<string>("");
  const [designers, setDesigners] = useState<Array<DesignerHit>>([]);
  const [suggestions, setSuggestions] = useState<Array<SuggestionHit>>([]);
  const [hasSetInitialQuery, setHasSetInitialQuery] = useState(false);
  const publicConfig = usePublicConfig();
  const client = useAlgoliaClient();

  const uuidFromUrl = getUuidFromUrl();

  const currentSearchQuery = useSearch(uuidFromUrl);

  const normalizeAndSetQuery = (q: string) => {
    setQuery(normalizeQuery(q));
  };

  useEffect(() => {
    if (currentSearchQuery.type === "Completed" && !hasSetInitialQuery) {
      const { value: search } = currentSearchQuery;
      if (search.query) {
        normalizeAndSetQuery(search.query);
      }
      setHasSetInitialQuery(true);
    }
  }, [currentSearchQuery, hasSetInitialQuery, uuidFromUrl]);

  useEffect(() => {
    const querySuggestionIndex =
      findAlgoliaIndexName("queries", "default")(publicConfig) || "";

    const designersIndex =
      findAlgoliaIndexName("designers", "default")(publicConfig) || "";

    const fetchSuggestions = async () => {
      if (queryCache[query]) {
        setSuggestions(queryCache[query].querySuggestions);
        setDesigners(queryCache[query].designers);
        return;
      }
      try {
        // Normalize the query to ensure it is in a standard format before passing it to the search client.
        const normalizedQuery = normalizeQuery(query);
        const { results } = await client.search<DesignerHit | SuggestionHit>([
          {
            indexName: querySuggestionIndex,
            query: normalizedQuery,
            params: {
              hitsPerPage: 8,
            },
          },
          {
            indexName: designersIndex,
            query: normalizedQuery,
            params: {
              hitsPerPage: 5,
            },
          },
        ]);
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setSuggestions(results[0].hits as Array<SuggestionHit>);

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setDesigners(results[1].hits as Array<DesignerHit>);

        queryCache[query] = {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          querySuggestions: results[0].hits as Array<SuggestionHit>,
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          designers: results[1].hits as Array<DesignerHit>,
        };
      } catch (err) {
        console.log(err);
      }
    };

    const debounceTimeout = setTimeout(fetchSuggestions, 300);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [query, client, publicConfig]);

  return {
    query,
    setQuery: normalizeAndSetQuery,
    results: {
      querySuggestions: suggestions,
      designers,
    },
  };
};

export default useHeaderSearch;
